import { DownloadLink } from "../DownloadLink";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useId, useRef, useState } from "react";
import {
  ArrowRight,
  BellRing,
  BrainCircuit,
  CalendarCheck2,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Database,
  Globe2,
  Mail,
  MessageCircle,
  RefreshCw,
  ShieldCheck,
  Target,
  UserRoundCheck,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import logoMarkSrc from "../../assets/clawease-logo-mark-vector.svg";
import { usePageActivity } from "../../hooks/usePageActivity";
import DentalHeroFlowDom from "./DentalHeroFlowDom";

type DentalHeroExecutionProps = {
  downloadPath: string;
  contactPath: string;
  onCtaClick: () => void;
};

type RequestCardProps = {
  delay: number;
  language: "EN" | "ES";
  reverseIcons?: boolean;
  shouldReduceMotion: boolean;
};

type OutcomeTileProps = {
  icon: LucideIcon;
  label: string;
};

type Point = {
  x: number;
  y: number;
};

type FlowGeometry = {
  aiBottom: Point;
  aiLeft: Point;
  aiRight: Point;
  channelRight: Point;
  channelTopLeft: Point;
  channelTopRight: Point;
  height: number;
  leftRequest: Point;
  outcomes: [Point, Point, Point];
  pmsLeft: Point;
  rightRequest: Point;
  width: number;
};

const glassSurface =
  "border border-white/70 bg-[linear-gradient(135deg,rgba(255,255,255,.58),rgba(255,255,255,.30))] shadow-[0_24px_70px_rgba(47,43,77,.10),0_4px_16px_rgba(47,43,77,.045),inset_0_1px_0_rgba(255,255,255,.90)] backdrop-blur-[26px] backdrop-saturate-[135%]";

function RequestCard({ delay, language, reverseIcons = false, shouldReduceMotion }: RequestCardProps) {
  const requestIcons = reverseIcons
    ? [Mail, MessageCircle, Globe2]
    : [Globe2, MessageCircle, Mail];

  return (
    <motion.div
      className={`relative min-h-[82px] overflow-hidden rounded-[20px] px-2.5 py-3 sm:min-h-[92px] sm:px-4 ${glassSurface}`}
      initial={{ opacity: shouldReduceMotion ? 1 : 0 }}
      animate={{ opacity: 1 }}
      transition={{
        opacity: { delay, duration: shouldReduceMotion ? 0 : 0.48 },
      }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(115deg,rgba(255,255,255,.24),transparent_56%)]"
      />
      <div className="relative flex items-center gap-2 sm:gap-3">
        <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-[13px] border border-white/75 bg-white/42 text-[#5b28ce] shadow-[0_8px_18px_rgba(83,50,152,.08)] backdrop-blur-xl sm:h-11 sm:w-11 sm:rounded-[14px]">
          <MessageCircle className="h-[19px] w-[19px]" strokeWidth={1.8} aria-hidden="true" />
          <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-white bg-[#20b783]">
            <CheckCircle2 className="h-2 w-2 text-white" strokeWidth={3} aria-hidden="true" />
          </span>
        </span>

        <span className="min-w-0 flex-1">
          <span className="block whitespace-nowrap pr-5 text-[10px] font-extrabold tracking-[-.02em] text-[#172038] sm:pr-0 sm:text-[14px]">Patient request</span>
          <span className="mt-1.5 flex items-center gap-1.5 text-[#4e5b78] sm:gap-2.5">
            {requestIcons.map((Icon, index) => (
              <Icon key={`${language}-${index}`} className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
            ))}
          </span>
        </span>

        <span className="absolute right-0 top-0 inline-flex shrink-0 items-center gap-1 rounded-full border border-white/70 bg-white/48 px-1.5 py-1 text-[8px] font-extrabold text-[#333b55] shadow-[0_3px_10px_rgba(43,39,69,.04)] sm:static sm:px-2 sm:text-[10px]">
          <Globe2 className="hidden h-3 w-3 text-[#455376] sm:block" strokeWidth={1.8} aria-hidden="true" />
          {language}
        </span>
      </div>
    </motion.div>
  );
}

function ChannelsGlyph() {
  return (
    <div className="relative mx-auto h-[64px] w-[84px] text-[#3949d8]" aria-hidden="true">
      <span className="absolute left-[41px] top-[22px] h-[26px] w-px bg-gradient-to-b from-[#5a3fdd]/75 to-[#417bdc]/45" />
      <span className="absolute left-[19px] top-[37px] h-px w-[48px] bg-gradient-to-r from-[#6540d8]/35 via-[#4e58db]/70 to-[#3b84df]/35" />
      <Globe2 className="absolute left-[31px] top-0 h-[23px] w-[23px]" strokeWidth={1.7} />
      <Globe2 className="absolute left-[3px] top-[26px] h-[22px] w-[22px]" strokeWidth={1.7} />
      <MessageCircle className="absolute right-[1px] top-[26px] h-[22px] w-[22px]" strokeWidth={1.7} />
      <Mail className="absolute bottom-0 left-[30px] h-[24px] w-[24px]" strokeWidth={1.7} />
    </div>
  );
}

function PmsGlyph() {
  return (
    <div className="relative mx-auto flex h-[62px] w-[72px] items-center justify-center rounded-[17px] border border-white/75 bg-white/42 text-[#3478df] shadow-[0_10px_28px_rgba(57,106,185,.09)]" aria-hidden="true">
      <Database className="h-8 w-8" strokeWidth={1.55} />
      <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border border-white/85 bg-[rgba(247,251,255,.82)] shadow-[0_5px_14px_rgba(58,97,166,.10)] backdrop-blur-lg">
        <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.8} />
      </span>
    </div>
  );
}

function OutcomeTile({ icon: Icon, label }: OutcomeTileProps) {
  return (
    <div className="relative flex min-h-[82px] flex-col items-center justify-center gap-1.5 rounded-[16px] border border-white/70 bg-white/34 px-2 py-2.5 text-center text-[#5368d8] shadow-[0_8px_22px_rgba(48,49,82,.055),inset_0_1px_0_rgba(255,255,255,.84)] backdrop-blur-[20px] backdrop-saturate-[130%] sm:min-h-[70px] sm:flex-row sm:justify-start sm:gap-2.5 sm:px-3 sm:text-left">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] border border-white/72 bg-white/40 shadow-[0_5px_15px_rgba(54,58,93,.05)] backdrop-blur-xl sm:h-10 sm:w-10 sm:rounded-[13px]">
        <Icon className="h-5 w-5" strokeWidth={1.65} aria-hidden="true" />
      </span>
      <span className="text-[8px] font-extrabold leading-[1.2] tracking-[-.01em] text-[#26324b] sm:text-[11px]">{label}</span>
    </div>
  );
}

function ExecutionStage() {
  const stageRef = useRef<HTMLDivElement>(null);
  const leftRequestRef = useRef<HTMLDivElement>(null);
  const rightRequestRef = useRef<HTMLDivElement>(null);
  const channelsRef = useRef<HTMLDivElement>(null);
  const aiNodeRef = useRef<HTMLDivElement>(null);
  const aiRef = useRef<HTMLSpanElement>(null);
  const pmsRef = useRef<HTMLDivElement>(null);
  const outcomeRefs = useRef<Array<HTMLDivElement | null>>([]);
  const id = useId().replace(/:/g, "");
  const shouldReduceMotion = Boolean(useReducedMotion());
  const pageActive = usePageActivity();
  const isInView = useInView(stageRef, { amount: 0.25 });
  const shown = Boolean(shouldReduceMotion || isInView);
  const active = Boolean(!shouldReduceMotion && isInView && pageActive);
  const [flowGeometry, setFlowGeometry] = useState<FlowGeometry | null>(null);

  const inputGradientId = `dental-input-gradient-${id}`;
  const systemGradientId = `dental-system-gradient-${id}`;
  const outputGradientId = `dental-output-gradient-${id}`;

  useEffect(() => {
    let frame = 0;
    let settleTimer = 0;

    const updateGeometry = () => {
      const stage = stageRef.current;
      const leftRequest = leftRequestRef.current;
      const rightRequest = rightRequestRef.current;
      const channels = channelsRef.current;
      const aiNode = aiNodeRef.current;
      const ai = aiRef.current;
      const pms = pmsRef.current;
      const outcomes = outcomeRefs.current;

      if (!stage || !leftRequest || !rightRequest || !channels || !aiNode || !ai || !pms || outcomes.length < 3 || outcomes.some((outcome) => !outcome)) return;

      const stageRect = stage.getBoundingClientRect();
      const point = (element: Element, xRatio: number, yRatio: number): Point => {
        const rect = element.getBoundingClientRect();
        return {
          x: rect.left - stageRect.left + rect.width * xRatio,
          y: rect.top - stageRect.top + rect.height * yRatio,
        };
      };

      setFlowGeometry({
        aiBottom: point(aiNode, 0.5, 1),
        aiLeft: point(ai, 0, 0.5),
        aiRight: point(ai, 1, 0.5),
        channelRight: point(channels, 1, 0.5),
        channelTopLeft: point(channels, 0.35, 0),
        channelTopRight: point(channels, 0.68, 0),
        height: stageRect.height,
        leftRequest: point(leftRequest, 0.5, 1),
        outcomes: [point(outcomes[0]!, 0.5, 0), point(outcomes[1]!, 0.5, 0), point(outcomes[2]!, 0.5, 0)],
        pmsLeft: point(pms, 0, 0.5),
        rightRequest: point(rightRequest, 0.5, 1),
        width: stageRect.width,
      });
    };

    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateGeometry);
    };

    const observedElements = [stageRef.current, leftRequestRef.current, rightRequestRef.current, channelsRef.current, aiNodeRef.current, aiRef.current, pmsRef.current, ...outcomeRefs.current].filter(Boolean) as Element[];
    const observer = new ResizeObserver(scheduleUpdate);
    observedElements.forEach((element) => observer.observe(element));
    window.addEventListener("resize", scheduleUpdate);
    scheduleUpdate();
    settleTimer = window.setTimeout(scheduleUpdate, 700);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer);
      observer.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, []);

  const connectorPaths = (() => {
    if (!flowGeometry) return null;

    const round = (value: number) => Math.round(value * 10) / 10;
    const pointText = (point: Point) => `${round(point.x)} ${round(point.y)}`;
    const channelTop: Point = {
      x: (flowGeometry.channelTopLeft.x + flowGeometry.channelTopRight.x) / 2,
      y: (flowGeometry.channelTopLeft.y + flowGeometry.channelTopRight.y) / 2,
    };
    const requestBottomY = Math.max(flowGeometry.leftRequest.y, flowGeometry.rightRequest.y);
    const requestBusY = requestBottomY + Math.max(14, (channelTop.y - requestBottomY) * 0.42);
    const requestBusMinX = Math.min(flowGeometry.leftRequest.x, flowGeometry.rightRequest.x, channelTop.x);
    const requestBusMaxX = Math.max(flowGeometry.leftRequest.x, flowGeometry.rightRequest.x, channelTop.x);
    const outcomeTopY = Math.min(...flowGeometry.outcomes.map((outcome) => outcome.y));
    const outcomeBusY = flowGeometry.aiBottom.y + Math.max(24, (outcomeTopY - flowGeometry.aiBottom.y) * 0.62);
    const requestPaths = [
      `M ${pointText(flowGeometry.leftRequest)} V ${round(requestBusY)}`,
      `M ${pointText(flowGeometry.rightRequest)} V ${round(requestBusY)}`,
      `M ${round(requestBusMinX)} ${round(requestBusY)} H ${round(requestBusMaxX)}`,
      `M ${round(channelTop.x)} ${round(requestBusY)} V ${round(channelTop.y)}`,
    ];
    const systemPaths = [
      `M ${pointText(flowGeometry.channelRight)} H ${round(flowGeometry.aiLeft.x)}`,
      `M ${pointText(flowGeometry.aiRight)} H ${round(flowGeometry.pmsLeft.x)}`,
    ];
    const outputPaths = [
      `M ${pointText(flowGeometry.aiBottom)} V ${round(outcomeBusY)}`,
      `M ${round(flowGeometry.outcomes[0].x)} ${round(outcomeBusY)} H ${round(flowGeometry.outcomes[2].x)}`,
      ...flowGeometry.outcomes.map((outcome) => (
        `M ${round(outcome.x)} ${round(outcomeBusY)} V ${round(outcome.y)}`
      )),
    ];

    return {
      outputPaths,
      requestPaths,
      systemPaths,
    };
  })();

  const revealTransition = () => ({
    delay: shouldReduceMotion ? 0 : 0.18,
    duration: shouldReduceMotion ? 0 : 0.46,
    ease: [0.22, 1, 0.36, 1] as const,
  });

  const renderStraightPaths = (
    paths: string[],
    gradientId: string,
    keyPrefix: string,
  ) => paths.map((path, index) => (
    <g key={`${keyPrefix}-${index}`}>
      <motion.path
        d={path}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="4"
        vectorEffect="non-scaling-stroke"
        initial={{ opacity: 0 }}
        animate={{ opacity: shown ? 0.12 : 0 }}
        transition={revealTransition()}
      />
      <motion.path
        d={path}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.25"
        vectorEffect="non-scaling-stroke"
        initial={{ opacity: 0 }}
        animate={{ opacity: shown ? 0.58 : 0 }}
        transition={revealTransition()}
      />
    </g>
  ));

  return (
    <div
      ref={stageRef}
      className="relative isolate order-2 w-full max-w-[650px] justify-self-end overflow-visible sm:aspect-[640/590]"
      role="img"
      aria-label="Two patient requests connect to Channels and ClawEase AI, which connects to the clinic PMS and completes appointment booking, recall reminders, and lead follow-up."
    >
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute inset-[4%_-3%_-1%_0] z-0 rounded-[34%] bg-[radial-gradient(circle_at_20%_22%,rgba(185,151,237,.24),transparent_44%),radial-gradient(circle_at_58%_48%,rgba(125,175,232,.21),transparent_50%),radial-gradient(circle_at_84%_76%,rgba(111,203,176,.18),transparent_43%),linear-gradient(135deg,rgba(196,178,232,.11),rgba(143,187,224,.10)_55%,rgba(128,210,187,.09))] blur-[46px]"
        animate={active ? { opacity: [0.76, 0.9, 0.76], scale: [1, 1.018, 1], x: [0, 2, 0] } : { opacity: 0.8, scale: 1, x: 0 }}
        transition={{ duration: 11.5, repeat: active ? Infinity : 0, ease: "easeInOut" }}
      />

      {connectorPaths && flowGeometry ? (
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-20 h-full w-full overflow-visible"
          viewBox={`0 0 ${flowGeometry.width} ${flowGeometry.height}`}
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id={inputGradientId} gradientUnits="userSpaceOnUse" x1={flowGeometry.width} y1="0" x2="0" y2={flowGeometry.height * 0.42}>
              <stop offset="0" stopColor="#7893e4" stopOpacity=".84" />
              <stop offset=".5" stopColor="#8b7cdd" stopOpacity=".90" />
              <stop offset="1" stopColor="#9b76d8" stopOpacity=".94" />
            </linearGradient>
            <linearGradient id={systemGradientId} gradientUnits="userSpaceOnUse" x1="0" y1={flowGeometry.height * 0.45} x2={flowGeometry.width} y2={flowGeometry.height * 0.45}>
              <stop offset="0" stopColor="#8b68d9" stopOpacity=".84" />
              <stop offset=".5" stopColor="#6578dd" stopOpacity=".90" />
              <stop offset="1" stopColor="#5198dd" stopOpacity=".86" />
            </linearGradient>
            <linearGradient id={outputGradientId} gradientUnits="userSpaceOnUse" x1={flowGeometry.width * 0.82} y1={flowGeometry.height * 0.48} x2={flowGeometry.width * 0.25} y2={flowGeometry.height * 0.82}>
              <stop offset="0" stopColor="#579fdf" stopOpacity=".84" />
              <stop offset=".55" stopColor="#61b8d4" stopOpacity=".82" />
              <stop offset="1" stopColor="#69c6ad" stopOpacity=".80" />
            </linearGradient>
          </defs>

          {renderStraightPaths(
            connectorPaths.requestPaths,
            inputGradientId,
            "input",
          )}
          {renderStraightPaths(
            connectorPaths.systemPaths,
            systemGradientId,
            "system",
          )}
          {renderStraightPaths(connectorPaths.outputPaths, outputGradientId, "output")}
        </svg>
      ) : null}

      <div className="relative z-30 grid grid-cols-2 gap-2.5 sm:contents">
        <div ref={leftRequestRef} className="sm:absolute sm:left-[1%] sm:top-[3%] sm:w-[44%]">
          <RequestCard delay={0.12} language="EN" shouldReduceMotion={shouldReduceMotion} />
        </div>
        <div ref={rightRequestRef} className="sm:absolute sm:right-0 sm:top-[5%] sm:w-[44%]">
          <RequestCard delay={0.22} language="ES" reverseIcons shouldReduceMotion={shouldReduceMotion} />
        </div>
      </div>

      <motion.div
        className={`relative z-10 mt-8 overflow-hidden rounded-[24px] px-3 py-5 sm:absolute sm:left-[4%] sm:right-[2%] sm:top-[31%] sm:mt-0 sm:h-[28%] sm:px-8 sm:py-4 ${glassSurface}`}
        initial={{ opacity: shouldReduceMotion ? 1 : 0 }}
        animate={{ opacity: shown ? 1 : 0 }}
        transition={{ delay: shouldReduceMotion ? 0 : 0.24, duration: shouldReduceMotion ? 0 : 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[linear-gradient(115deg,rgba(255,255,255,.22),transparent_60%)]" />
        <div className="relative z-30 grid h-full grid-cols-[1fr_.82fr_1fr] items-center gap-2 sm:gap-5">
          <div className="text-center">
            <div ref={channelsRef} className="mx-auto w-fit"><ChannelsGlyph /></div>
            <span className="mt-1.5 block text-[9px] font-extrabold uppercase tracking-[.08em] text-[#34416d] sm:text-[10px]">Channels</span>
          </div>

          <div ref={aiNodeRef} className="relative flex flex-col items-center justify-center gap-1">
            <motion.span
              aria-hidden="true"
              className="absolute top-[-13px] h-[86px] w-[86px] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,.62),rgba(255,255,255,.14)_50%,transparent_74%)] blur-[12px]"
              animate={active ? { opacity: [0.52, 0.72, 0.52], scale: [1, 1.025, 1] } : { opacity: 0.56, scale: 1 }}
              transition={{ duration: 5.8, repeat: active ? Infinity : 0, ease: "easeInOut" }}
            />
            <span ref={aiRef} className="relative flex h-[54px] w-[54px] items-center justify-center rounded-full border border-white/82 bg-white/48 shadow-[0_13px_32px_rgba(83,56,161,.10),inset_0_1px_0_rgba(255,255,255,.94)] backdrop-blur-xl sm:h-[60px] sm:w-[60px]">
              <img src={logoMarkSrc} alt="" className="h-8 w-8 sm:h-9 sm:w-9" />
            </span>
            <span className="relative whitespace-nowrap text-[9px] font-extrabold tracking-[-.02em] text-[#202b44] sm:text-[10px]">
              ClawEase <span className="text-[#6d35cf]">AI</span>
            </span>
          </div>

          <div className="text-center">
            <div ref={pmsRef} className="mx-auto w-fit"><PmsGlyph /></div>
            <span className="mt-1.5 block text-[9px] font-extrabold uppercase tracking-[.08em] text-[#34416d] sm:text-[10px]">PMS</span>
          </div>
        </div>
      </motion.div>

      <motion.div
        className={`relative z-10 mt-3 overflow-hidden rounded-[24px] p-3.5 sm:absolute sm:bottom-[1%] sm:left-0 sm:right-0 sm:mt-0 sm:h-[34%] sm:p-4 ${glassSurface}`}
        initial={{ opacity: shouldReduceMotion ? 1 : 0 }}
        animate={{ opacity: shown ? 1 : 0 }}
        transition={{ delay: shouldReduceMotion ? 0 : 0.34, duration: shouldReduceMotion ? 0 : 0.58, ease: [0.22, 1, 0.36, 1] }}
      >
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[linear-gradient(125deg,rgba(255,255,255,.22),transparent_62%)]" />

        <div className="relative z-30 mt-10 grid grid-cols-3 gap-2 sm:gap-2.5">
          <div ref={(element) => { outcomeRefs.current[0] = element; }}><OutcomeTile icon={CalendarCheck2} label="Appointment booked" /></div>
          <div ref={(element) => { outcomeRefs.current[1] = element; }}><OutcomeTile icon={BellRing} label="Recall reminder sent" /></div>
          <div ref={(element) => { outcomeRefs.current[2] = element; }}><OutcomeTile icon={UserRoundCheck} label="Lead follow-up completed" /></div>
        </div>

        <div className="relative z-30 mt-3 grid grid-cols-4 border-t border-[#dce4ee]/72 pt-2.5">
          {[
            { icon: BrainCircuit, label: "Smart" },
            { icon: Target, label: "Accurate" },
            { icon: Clock3, label: "24/7" },
            { icon: CheckCircle2, label: "Executes" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center justify-center gap-1.5 text-[#34416a]">
              <Icon className="h-3.5 w-3.5 text-[#5368d8]" strokeWidth={1.7} aria-hidden="true" />
              <span className="text-[8px] font-extrabold uppercase tracking-[.05em] sm:text-[9px]">{label}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

export function DentalHeroExecution({ downloadPath, contactPath, onCtaClick }: DentalHeroExecutionProps) {
  return (
    <section id="dental-overview" className="relative overflow-hidden bg-[#fbfaf6] px-5 pb-14 pt-28 sm:px-10 sm:pb-16 sm:pt-32 lg:px-12 lg:pb-[72px] lg:pt-[140px] xl:min-h-[700px] xl:px-16">
      <div aria-hidden="true" className="pointer-events-none absolute -left-48 top-32 h-96 w-96 rounded-full bg-[#e8f2ff]/60 blur-[100px]" />

      <div className="dental-content dental-content--hero relative grid gap-8 xl:grid-cols-[minmax(0,1.04fr)_minmax(520px,.96fr)] xl:items-center xl:gap-10">
        <div className="min-w-0 xl:pb-4">
          <h1 className="max-w-[620px] text-[42px] font-extrabold leading-[1.04] tracking-[-.045em] text-[#172033] sm:text-[54px] lg:text-[58px] xl:text-[58px]">
            <span className="block">The Dental AI</span>{" "}
            <span className="block">Receptionist</span>{" "}
            <span className="block text-[#6d35cf]">
              <span className="block">that turns inquiries</span>{" "}
              <span className="block">into appointments.</span>
            </span>
          </h1>
          <p className="mt-7 max-w-[680px] text-[16px] font-medium leading-8 text-[#596176] sm:text-[17px]">
            ClawEase responds immediately, captures the required details, and prepares the clinic-approved next step—while your team stays focused on patients.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
            <DownloadLink
              to={downloadPath}
              onClick={onCtaClick}
              className="inline-flex min-h-12 min-w-[160px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[12px] px-6 text-[14px] font-semibold tracking-[-.01em] text-white no-underline shadow-[0_14px_32px_rgba(93,49,191,.19)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_38px_rgba(93,49,191,.24)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6d35cf]/55 focus-visible:ring-offset-2 motion-reduce:transition-none"
              style={{ background: "linear-gradient(135deg, #5d2cc3 0%, #7834cf 58%, #d638c9 100%)" }}
            >
              Try it now
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </DownloadLink>
            <span className="inline-flex items-center gap-2 whitespace-nowrap text-[14px] leading-6">
              <span className="text-[13px] font-normal text-[#8b8493]">or</span>
              <Link
                to={contactPath}
                onClick={onCtaClick}
                className="inline-flex min-h-11 items-center rounded-sm font-semibold tracking-[-.015em] text-[#56436a] no-underline transition-colors duration-200 hover:text-[#7834cf] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6d35cf]/45 focus-visible:ring-offset-4 motion-reduce:transition-none"
              >
                contact us
              </Link>
            </span>
          </div>

          <ul
            className="mt-7 flex min-h-[54px] max-w-[600px] flex-wrap items-center gap-x-7 gap-y-3 sm:gap-x-9"
            aria-label="ClawEase highlights"
          >
            {["Free to start", "24/7 Intake", "AI-powered"].map((label) => (
              <li
                key={label}
                className="inline-flex items-center gap-2 text-[14px] font-medium leading-6 text-[#737b86]"
              >
                <ShieldCheck
                  className="h-6 w-6 shrink-0 fill-[#c8cdd4]/20 text-[#a6adb7]/80"
                  strokeWidth={1.5}
                  aria-hidden="true"
                />
                <span className="whitespace-nowrap tracking-[-.01em]">{label}</span>
              </li>
            ))}
          </ul>
        </div>

        <DentalHeroFlowDom />
      </div>
    </section>
  );
}
